import React, { FunctionComponent, ReactElement } from 'react';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import { Link } from '@empiriecom/module-components/Link';
import { FormattedMessage } from 'react-intl';
import { renderFont } from '@empiriecom/module-components/ThemeProvider';
import { SeoDataInterface, SeoLinkInterface } from '@/interfaces/index';

interface SeoItemProps {
  fullWidthItem?: boolean;
  headline: string | ReactElement;
  links: SeoLinkInterface[];
  testId?: string;
  type?: 'default' | 'primary';
}

interface SeoWrapperProps {
  data: SeoDataInterface;
}

const SeoLinkListWrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-flow: row wrap;
`;

const SeoLinksContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
`;

const SeoLink = styled.div<{ primaryMaxWidth: string | null; fullWidth: boolean; mobile: boolean }>`
  ${({ mobile }) => (mobile ? 'width: 50%;' : 'width: 33%;')};
  ${({ fullWidth }) => fullWidth && 'width: 100%;'};
  ${({ primaryMaxWidth }) => primaryMaxWidth !== null && `max-width: ${primaryMaxWidth}`};
  padding-right: 1.25rem;
  font-size: 0.813rem;
  overflow: hidden;
  text-overflow: ellipsis;
  a,
  a:hover {
    text-decoration: none;
    white-space: nowrap;
    color: inherit;
  }
`;

const SeoItemHeadline = styled.div<{ mobile: boolean }>`
  ${({ theme }) => renderFont('base', 'normal', theme)};
  font-size: ${({ mobile }) => (mobile ? '1.313rem;' : '1.625rem;')};
  line-height: 1.75rem;
  text-align: center;
  margin: 1rem;
`;

const SeoLinkList = styled.div<{ fullWidthItem: boolean; mobile: boolean }>`
  width: ${({ fullWidthItem }) => (fullWidthItem ? '100' : '50')}%;
  padding: ${({ mobile }) => (mobile ? '0 1.625rem;' : '0 0.625rem;')};
  margin-bottom: 1.25rem;
`;

const SeoItem: FunctionComponent<SeoItemProps> = ({
  fullWidthItem = false,
  headline,
  links,
  testId,
  type = 'default',
}): ReactElement => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const primaryMaxWidth = useMedia({
    desktop: '16%;',
    mobile: '50%;',
    tablet: '33%;',
  });
  if (links && links.length > 0) {
    return (
      <SeoLinkList
        data-testid={`seo-link-list-${testId}`}
        fullWidthItem={fullWidthItem}
        mobile={isMobile}
      >
        {headline && (
          <SeoItemHeadline aria-level={3} role="heading" mobile={isMobile}>
            {headline}
          </SeoItemHeadline>
        )}
        <SeoLinksContainer>
          {links.map((value) => (
            <SeoLink
              data-testid="seo-link-container"
              primaryMaxWidth={type === 'primary' ? primaryMaxWidth : null}
              fullWidth={links.length === 1}
              key={value.url}
              mobile={isMobile}
            >
              <Link align="right" as="a" href={value.url} title={value.alt} to={value.url}>
                {value.alt}
              </Link>
            </SeoLink>
          ))}
        </SeoLinksContainer>
      </SeoLinkList>
    );
  }
  return null;
};

export const SeoElement: FunctionComponent<SeoWrapperProps> = ({ data }) => {
  const fullWidth = useMedia({ desktop: false, mobile: true, tablet: true });
  return (
    <SeoLinkListWrapper>
      {data.alternateBrands && (
        <SeoItem
          fullWidthItem
          headline={
            <FormattedMessage
              id="seo.alternatebrands.headline"
              defaultMessage="Andere Marken"
              description="The headline for the seo alternate links"
            />
          }
          links={data.alternateBrands}
          testId="alt-brands"
          type="primary"
        />
      )}
      {data.relationLinks && (
        <SeoItem
          fullWidthItem={fullWidth}
          links={data.relationLinks}
          testId="relation-links"
          headline={
            <FormattedMessage
              id="seo.similarLinks.headline"
              defaultMessage="Ähnliche Kategorien"
              description="The headline for the seo similar links"
            />
          }
        />
      )}
      {data.similarLinks && (
        <SeoItem
          fullWidthItem={fullWidth}
          links={data.similarLinks}
          testId="similar-links"
          headline={
            <FormattedMessage
              id="seo.relationlinks.headline"
              defaultMessage="Empfohlene Kategorien"
              description="The headline for the seo relation links"
            />
          }
        />
      )}
      {data.topLinks && (
        <SeoItem
          fullWidthItem={fullWidth}
          links={data.topLinks}
          testId="top-links"
          headline={
            <FormattedMessage
              id="seo.topLinks.headline"
              defaultMessage="Shopping Tipps"
              description="The headline for the seo shopping tip links"
            />
          }
        />
      )}
    </SeoLinkListWrapper>
  );
};

export default SeoElement;
